import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import { setCookie } from 'nookies'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import LeadForm, { type LeadFormSource } from 'src/components/LeadForm'
import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import type { LeadFormValues } from 'src/components/LeadForm/useLeadFormState'
import ModalDrawer from 'src/components/ModalDrawer'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { useListingVariant } from 'src/lib/listingVariants'
import type { LeadFormChannel } from 'src/lib/listingVariants/types'
import {
  CustomerType,
  EventAction,
  IConfiguration,
  ILocation,
  IMutation,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  StorageTime
} from 'src/types/graphql'
import useAddToCart, { useGenerateLead } from 'src/utils/pixels/common'
import { getClickIdsFromCookies } from 'src/utils/utms'
import { claimWaSessionToken } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail($customerInfo: CustomerInfoInput!) {
    sendCustomerInfoEmail(customerInfo: $customerInfo)
  }
`

type Props = {
  location: ILocation
  configuration: IConfiguration
}

/**
 * Mobile-only inline CTA block that sits in the listing body and gives
 * renters who don't want to use WhatsApp a structured email path. The
 * WhatsApp pill remains the primary CTA on the PriceCard (desktop right
 * rail + mobile sticky bar) — this block is the secondary email lane on
 * mobile only, where the sticky bar is too tight to host both options.
 *
 * Desktop equivalent: an outlined "Solicitar por correo" button rendered
 * directly inside PriceCard below the WA pill (see PriceCard.tsx). Both
 * paths open the same LeadForm drawer; this component duplicates the
 * submit handler today — extracting to a shared hook is a follow-up.
 *
 * Gated by the variant's `primaryCta.action === 'open-whatsapp'` so it
 * only renders for Broker / Traditional listings post-flip. Monthly and
 * Fleet variants are untouched.
 */
export default function LeadFormInlineCta({ location, configuration }: Props) {
  const { t } = useTranslation('details')
  const logger = useLogger()
  const { enqueueSnackbar } = useSnackbar()
  const { trackEvent } = useTrackEvent()
  const generateLead = useGenerateLead()
  const handleAddToCart = useAddToCart()
  const variant = useListingVariant()
  const [sendCustomerInfoEmail] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL)

  const [isOpen, setIsOpen] = useState(false)

  const isTraditionalMode = variant.meta.variantKey === 'traditional'
  const leadSource: LeadFormSource = isTraditionalMode
    ? 'listing_traditional_modal'
    : 'listing_broker_modal'

  const handleSubmit = useCallback(
    async (values: LeadFormValues, channel: LeadFormChannel) => {
      const trimmedName = values.name?.trim() ?? ''
      const trimmedEmail = values.email?.trim() ?? ''
      const trimmedPhone = values.phone?.trim() ?? ''
      try {
        await sendCustomerInfoEmail({
          variables: {
            customerInfo: {
              name: trimmedName,
              lastname: '',
              email: trimmedEmail,
              phone: trimmedPhone,
              message: values.additionalInfo ?? '',
              interest: Interest.Rent,
              customerType: isTraditionalMode
                ? CustomerType.RentIndividual
                : CustomerType.RentBroker,
              locationId: location.id,
              configId: configuration.id,
              locationType: configuration.locationType ?? undefined,
              leadSource,
              storageTime: values.when
                ? (values.when as StorageTime)
                : undefined,
              // Injected by LeadForm's BotID pre-flight — server keeps the
              // lead but skips the host notification when flagged.
              botIdFlagged: values.botIdFlagged
            }
          }
        })
      } catch (error) {
        logger.error('Error creating lead from inline body CTA', { error })
        enqueueSnackbar(
          'Ha ocurrido un error al enviar la información, por favor intenta de nuevo',
          { variant: 'error' }
        )
        setCookie(null, 'lead', '', { maxAge: 0 })
        throw error
      }

      setCookie(
        null,
        'lead',
        JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone,
          additionalInfo: values.additionalInfo ?? ''
        }),
        { maxAge: 30 * 24 * 60 * 60, path: '/' }
      )

      trackEvent({
        action: EventAction.BrokerSignup,
        locationId: location.id,
        configurationId: configuration.id,
        subType: isTraditionalMode ? 'traditional_contact' : 'broker_contact'
      })
      generateLead({
        method: isTraditionalMode ? 'traditional_contact' : 'broker',
        locationType: configuration.locationType ?? undefined,
        dealType: configuration.dealType,
        monthlyPrice: configuration.price ?? undefined,
        channel
      })

      if (channel === 'whatsapp') {
        const msg = buildLeadFormWaMessage({
          variant: { meta: variant.meta, leadForm: variant.leadForm },
          location,
          configuration,
          values: {
            name: trimmedName,
            when: values.when,
            additionalInfo: values.additionalInfo
          },
          locale: 'es',
          sessionToken: claimWaSessionToken(),
          clickIds: getClickIdsFromCookies()
        })
        const url = buildWhatsappUrl(msg)
        if (typeof window !== 'undefined') {
          window.open(url, '_blank')
        }
      }
    },
    [
      sendCustomerInfoEmail,
      isTraditionalMode,
      location,
      configuration,
      leadSource,
      logger,
      enqueueSnackbar,
      trackEvent,
      generateLead,
      variant.meta,
      variant.leadForm
    ]
  )

  // Render gate — only show for the WA-pill variant (broker / traditional
  // post-flip). Monthly / Fleet variants have their own funnels.
  if (variant.priceCard.primaryCta.action !== 'open-whatsapp') return null

  return (
    <div className="md:hidden">
      <div className="mt-8 border border-ink-100/15 bg-cream-100 p-5">
        <h3 className="font-display text-base leading-tight font-semibold text-ink-700">
          {t('CTA.EMAIL_INLINE_HEADING')}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-200">
          {t('CTA.EMAIL_INLINE_BODY')}
        </p>
        <button
          type="button"
          onClick={() => {
            // Mirror PriceCard.handleOnBrokerClick: fire intent signals
            // (a) ES ADD_TO_CART + (b) PostHog add_to_cart_intent with
            // skipPixelFanout=true so the downstream LeadForm first-focus
            // fires (c) GA4/Meta/UET/LinkedIn fanout. Without this the
            // mobile body-block engagement is invisible to the marketing-
            // report Excel and PostHog `add_to_cart_intent` dashboards.
            handleAddToCart(
              isTraditionalMode ? 'traditional_contact' : 'broker_contact',
              location,
              configuration,
              1,
              '',
              { skipPixelFanout: true }
            )
            setIsOpen(true)
          }}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink-100/40 bg-white-500 px-6 py-2.5 text-sm font-semibold text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
        >
          {t('CTA.EMAIL_SECONDARY')}
        </button>
      </div>
      <ModalDrawer isOpen={isOpen} setIsOpen={(open) => setIsOpen(open)}>
        <LeadForm
          container="mobile-sheet"
          leadSource={leadSource}
          location={location}
          configuration={configuration}
          onSubmit={handleSubmit}
          onClose={() => setIsOpen(false)}
          // User opened this drawer from the body's email CTA — pin the
          // primary channel to email so Step 2 renders the blue email pill
          // on top and the WhatsApp link as secondary. Without this the
          // variant's `primaryChannel: 'whatsapp'` would carry through and
          // the email button would feel like a misroute.
          primaryChannelOverride="email"
        />
      </ModalDrawer>
    </div>
  )
}
